import React, { useEffect, useState } from "react";
import { X, Save, ShieldCheck, Sparkles, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Modal shown when an anonymous guest taps "Save itinerary" in the chat.
 * Creates a free Janté account AND saves the trip in one atomic call so the
 * guest can return and recall the trip by their reference number.
 *
 * Returning visitors can switch to a sign-in tab on the same modal.
 */
export default function SaveTripModal({
  open,
  onClose,
  onSaved, // (itinerary_id) => void
  destination,
  tripRef,
  budget,
  messages, // optional: chat transcript for notes
  travelers = 2,
}) {
  const [mode, setMode] = useState("signup"); // 'signup' | 'signin'
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    marketing_pref: "all_communications",
  });

  useEffect(() => {
    if (!open) return;
    setMode("signup");
  }, [open]);

  if (!open) return null;

  const today = new Date();
  const start = today.toISOString().slice(0, 10);
  const end = new Date(today.getTime() + 5 * 86400000).toISOString().slice(0, 10);
  const tripTitle = `Trip to ${destination || "Custom Destination"}`;
  const transcript = (messages || [])
    .map((m) => `${m.role === "user" ? "Guest" : "Sasha"}: ${m.text}`)
    .join("\n\n");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (mode === "signup") {
        const r = await api.post("/auth/register-and-save", {
          email: form.email,
          password: form.password,
          name: form.name,
          phone: form.phone || null,
          marketing_pref: form.marketing_pref,
          title: tripTitle,
          destination: destination || "Custom Destination",
          start_date: start,
          end_date: end,
          travelers,
          notes: transcript || null,
          items: [
            { type: "flight", title: `Flights to ${destination || "your destination"}`, provider: "preferred_flights" },
            { type: "car", title: "Ground transport", provider: "preferred_cars" },
            { type: "activity", title: "Curated experiences", provider: "preferred_activities" },
          ],
          budget: budget || null,
          trip_ref: tripRef || null,
        });
        try {
          window.localStorage.setItem("sasha_token", r.data.token);
        } catch {
          /* storage unavailable */
        }
        toast.success(`Welcome to Janté — your trip ${r.data.recall_code} is saved.`);
        onSaved?.(r.data.itinerary_id);
        return;
      }
      // sign-in branch
      const r = await api.post("/auth/login", {
        email: form.email,
        password: form.password,
      });
      try {
        window.localStorage.setItem("sasha_token", r.data.token);
      } catch {
        /* storage unavailable */
      }
      // Now save the trip on top of the existing account
      const ir = await api.post("/itineraries", {
        title: tripTitle,
        destination: destination || "Custom Destination",
        start_date: start,
        end_date: end,
        travelers,
        notes: transcript || null,
        items: [
          { type: "flight", title: `Flights to ${destination || "your destination"}`, provider: "preferred_flights" },
          { type: "car", title: "Ground transport", provider: "preferred_cars" },
          { type: "activity", title: "Curated experiences", provider: "preferred_activities" },
        ],
        budget: budget || null,
        recall_code: tripRef || undefined,
      });
      toast.success("Trip saved to your portal.");
      onSaved?.(ir.data.id);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Could not save — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-[var(--emerald)]/45 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
      data-testid="save-trip-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-[var(--ivory)] max-w-xl w-full p-7 sm:p-9 relative shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[var(--muted)] hover:text-[var(--emerald)]"
          aria-label="Close"
          data-testid="save-trip-close"
        >
          <X size={18} />
        </button>

        <Save size={20} strokeWidth={1.25} className="text-[var(--gold)]" />
        <div className="overline mt-3">Save your trip</div>
        <h2 className="font-display text-3xl sm:text-4xl text-[var(--emerald)] mt-2 leading-tight">
          A home for every trip you imagine.
        </h2>
        {tripRef && (
          <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-[var(--brand-red)]">
            Trip reference {tripRef}
          </div>
        )}
        <p className="text-sm text-[var(--ink-2)] mt-3">
          Create a complimentary Janté account so you can return any time and pull this trip up by its reference number.
        </p>

        {/* Subtle benefits — never overwhelming, just enough to seed value. */}
        <ul className="mt-5 space-y-2 text-sm text-[var(--ink-2)]">
          <li className="flex items-start gap-2">
            <Sparkles size={14} strokeWidth={1.25} className="text-[var(--gold)] mt-1 shrink-0" />
            Every trip stored under your reference number, retrievable any time.
          </li>
          <li className="flex items-start gap-2">
            <ShieldCheck size={14} strokeWidth={1.25} className="text-[var(--gold)] mt-1 shrink-0" />
            Free forever — paid memberships unlock priority concierge & member-only rates.
          </li>
          <li className="flex items-start gap-2">
            <KeyRound size={14} strokeWidth={1.25} className="text-[var(--gold)] mt-1 shrink-0" />
            One login. Sasha remembers your preferences across every visit.
          </li>
        </ul>

        {/* Tabs */}
        <div className="mt-7 flex border-b border-[var(--emerald)]/15" data-testid="save-trip-tabs">
          <button
            type="button"
            onClick={() => setMode("signup")}
            className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] transition-colors ${
              mode === "signup"
                ? "text-[var(--emerald)] border-b-2 border-[var(--gold)]"
                : "text-[var(--muted)] hover:text-[var(--emerald)]"
            }`}
            data-testid="save-trip-tab-signup"
          >
            Create account
          </button>
          <button
            type="button"
            onClick={() => setMode("signin")}
            className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] transition-colors ${
              mode === "signin"
                ? "text-[var(--emerald)] border-b-2 border-[var(--gold)]"
                : "text-[var(--muted)] hover:text-[var(--emerald)]"
            }`}
            data-testid="save-trip-tab-signin"
          >
            I already have one
          </button>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          {mode === "signup" && (
            <div>
              <div className="overline mb-1">Name</div>
              <input
                className="lux-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="save-trip-name"
              />
            </div>
          )}
          <div>
            <div className="overline mb-1">Email</div>
            <input
              type="email"
              required
              className="lux-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="save-trip-email"
            />
          </div>
          <div>
            <div className="overline mb-1">Password</div>
            <input
              type="password"
              required
              minLength={6}
              className="lux-input"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Minimum 6 characters"
              data-testid="save-trip-password"
            />
          </div>
          {mode === "signup" && (
            <>
              <div>
                <div className="overline mb-1">Phone (optional)</div>
                <input
                  className="lux-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="save-trip-phone"
                />
              </div>
              <div>
                <div className="overline mb-1">Communication preference</div>
                <select
                  className="lux-input"
                  value={form.marketing_pref}
                  onChange={(e) => setForm({ ...form, marketing_pref: e.target.value })}
                  data-testid="save-trip-marketing"
                >
                  <option value="all_communications">All communications</option>
                  <option value="vacation_only">Vacation Reference Only</option>
                  <option value="no_marketing">No Marketing</option>
                </select>
              </div>
            </>
          )}

          <div className="pt-2 flex items-center gap-3 flex-wrap">
            <button
              type="submit"
              disabled={busy}
              className="btn-primary"
              data-testid="save-trip-submit"
            >
              {busy
                ? "Working…"
                : mode === "signup"
                ? "Create account & save trip"
                : "Sign in & save trip"}
            </button>
            <button type="button" onClick={onClose} className="btn-ghost" data-testid="save-trip-cancel">
              Maybe later
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
